import React, { useState } from 'react'
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import PatternVersionTitle from '../../../components/backtests/PatternVersionTitle'
import StandardBadge from '../../../components/StandardBadge'
import { useAlertContext } from '../contexts/AlertContext'
import { TemplateCard } from '../hooks/useTemplateCards'

// Matches AlertCard's fixed height so the grid rows stay even.
const CARD_HEIGHT = 360

type AlertTemplateCardProps = {
  template: TemplateCard
}

/**
 * A standard alert template rendered as a card in the alerts pane.
 *
 * The member does not own an alert for this template yet — Activate calls
 * createAlertFromTemplate, which creates it switched on when the member has room for another
 * active alert, and the real AlertCard replaces this one as soon as the Firestore listener
 * delivers it.
 *
 * Deliberately not AlertCard: there is no tradingPlans doc yet, so the notification
 * toggles, history listener and auto-trade menu have nothing to act on, and the card
 * must stay out of the tour's `.alert-card` selectors.
 */
function AlertTemplateCard({ template }: AlertTemplateCardProps) {
  const theme = useTheme()
  const { createFromTemplate } = useAlertContext()
  const [activating, setActivating] = useState(false)

  const handleActivate = async () => {
    if (activating) return
    setActivating(true)
    let created = false
    try {
      created = await createFromTemplate(template)
    } finally {
      // On success the button stays disabled: the alerts listener replaces this card with
      // the real alert card, and re-enabling first would let a second click in between.
      if (!created) setActivating(false)
    }
  }

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: CARD_HEIGHT,
        '--Paper-overlay': 'none !important',
        // Dashed, so an offer reads differently from an alert the member owns
        border: `1px dashed ${theme.palette.background.custom4}`,
        overflow: 'visible',
      }}
      className={'alert-template-card'}
      data-template-id={template.id}
    >
      {/* Header — mirrors AlertCard's so the two read as the same object */}
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={0.5}
        sx={{
          padding: '8px 12px 4px 12px',
          backgroundColor: theme.palette.background.custom4,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Badge sits where AlertCard puts its on/off switch */}
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} width="100%">
          <Box display="flex" alignItems="center" overflow="hidden" gap={1} minWidth={0}>
            <NotificationsIcon fontSize={'small'} />
            <Typography
              variant="body1"
              color="text.primary"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {template.name}
            </Typography>
          </Box>
          <StandardBadge label="DTU Basic" />
        </Box>

        <Box display="flex" alignItems="center" gap={1} minWidth={0} width="100%">
          <AccountTreeIcon fontSize={'small'} sx={{ color: theme.palette.text.secondary }} />
          {template.pattern ? (
            // The symbol is the template's: it's what gets baked into the created alert
            <PatternVersionTitle
              pattern={template.pattern}
              symbol={template.symbol}
              color="text.secondary"
            />
          ) : (
            <Typography
              variant="body1"
              color="text.secondary"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {template.symbol}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={
            activating ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <NotificationsActiveIcon />
            )
          }
          disabled={activating}
          onClick={handleActivate}
        >
          {activating ? 'Activating…' : 'Activate'}
        </Button>

        <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
          Activate this alert to start getting notifications.
        </Typography>
      </Box>
    </Card>
  )
}

export default AlertTemplateCard
